import axios from "axios";
import { crumb } from "./errorReporter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Double-submit CSRF: echo the csrf_token cookie back as a header on writes.
export function csrfToken() {
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? m[1] : "";
}
api.interceptors.request.use((config) => {
  const t = csrfToken();
  if (t) config.headers["X-CSRF-Token"] = t;
  config._t0 = Date.now();
  return config;
});
// Breadcrumbs for the error tracker: every API call with status + duration.
api.interceptors.response.use(
  (res) => {
    crumb("api", { m: (res.config.method || "").toUpperCase(), url: res.config.url, status: res.status, ms: Date.now() - (res.config._t0 || Date.now()) });
    return res;
  },
  (error) => {
    const c = error.config || {};
    crumb("api", { m: (c.method || "").toUpperCase(), url: c.url, status: error.response?.status || "network", ms: Date.now() - (c._t0 || Date.now()) });
    if (error.response?.status === 402 && error.response.data?.code) {
      window.dispatchEvent(new CustomEvent("kingly:plan-block", { detail: error.response.data }));
    }
    return Promise.reject(error);
  }
);

/** WebSocket URL for an /api path, carrying the CSRF token the server requires. */
export function wsUrlFor(path) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const proto = base.startsWith("https") ? "wss" : "ws";
  return `${proto}://${base.replace(/^https?:\/\//, "")}${path}?csrf=${encodeURIComponent(csrfToken())}`;
}

// ---- Silent session refresh ----
// On a 401, try POST /auth/refresh once (dedupe concurrent refreshes),
// then retry the original request. Keeps mobile users logged in for 60 days.
let refreshPromise = null;
const AUTH_SKIP = ["/auth/login", "/auth/refresh", "/auth/register", "/auth/logout"];

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const url = original?.url || "";
    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !AUTH_SKIP.some((p) => url.includes(p))
    ) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = api.post("/auth/refresh").finally(() => {
            refreshPromise = null;
          });
        }
        await refreshPromise;
        return api(original);
      } catch (e) {
        // Session dead (signed out remotely / expired): tell the app shell so it drops to /login.
        if (e?.response?.status === 401) window.dispatchEvent(new CustomEvent("kingly:auth-lost"));
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

/** Convert a stored image reference (URL like "/api/files/xxx" or a base64
 * data URL) into a value safe for `<img src="...">`. */
export function imgSrc(v) {
  if (!v) return "";
  if (typeof v !== "string") return "";
  if (v.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${v}`;
  return v;
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
